import React, { useState, useCallback, useEffect } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import { tracker } from 'app-components/utils/core';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { filtersActions, filtersSelectors } from 'app-components/flux/filters';
import { freeToSpendActions, freeToSpendSelectors } from 'app-components/flux/free-to-spend';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import AmountField from 'components/QuickenControls/AmountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import usePlannedCategoryChecker from 'hooks/usePlannedCategoryChecker';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { deletePlannedSpendingDialog } from '../DeletePlannedSpending/actions';

const useStyles = makeStyles()((theme) => ({
  textSecondaryColor: {
    color: theme.palette.text.secondary,
  },
  dialog: {
    height: 555,
    width: 850,
    margin: 'auto',
  },
  catAndAmt: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  helperText: {
    width: 310,
    // marginBottom: 24,
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
  },
  spacer: {
    width: 24,
  },
  paddingLeft: {
    paddingLeft: 8,
  },
  widthOfTextField: {
    width: 389,
  },
  amountField: {
    textAlign: 'left !important',
  },
  radio: {
    paddingTop: 16,
  },
  dialogContent: {
    overflow: 'hidden',
    paddingTop: '16px !important',
  },
}));

const defaultPlanned = Object.freeze({ name: '', targetAmount: 0, recurring: false });

function EditPlannedSpending(props) {
  const { freeToSpendId, cardIndex, startDate, filterId, spendingThisMonth, location } = props;
  const [suggestedCategories, setSuggestedCategories] = usePreferenceV2('suggestedPSICategoryKnownIds');

  const plannedItems = useSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemsByF2SId(state, freeToSpendId),
  );
  const filter = useSelector((state) => filtersSelectors.getFilter(state, filterId));

  const { classes, cx } = useStyles();
  const freeToSpend = useSelector((state) => freeToSpendSelectors.getFreeToSpendById(state, freeToSpendId));
  const leftToSpend = useSelector((state) => freeToSpendSelectors.getLeftToSpend(state, freeToSpend));
  const parentCoaId = useSelector((state) => categoriesSelectors.getParentFromCoaList(state, filter));
  const doAllItemsExist = useSelector((state) =>
    freeToSpendSelectors.doPlannedSpendingItemsForDateAllHaveIds(state, startDate),
  );
  const calculatedPlannedAmount = useSelector((state) =>
    freeToSpendSelectors.getCalculatedPlannedSpending(state, freeToSpendId),
  );
  const plannedItem = plannedItems?.get(cardIndex) || defaultPlanned;
  const realTargetAmount =
    plannedItem.overwrittenTargetAmount === 0 ? 0 : plannedItem.overwrittenTargetAmount || plannedItem.targetAmount;
  const { name, recurring } = plannedItem;
  const [nameState, setName] = useState(name);
  const [targetAmountState, setTarget] = useState(realTargetAmount);
  const [editType, setEditType] = useState(recurring ? 'monthly' : 'onetime');
  // eslint-disable-next-line no-unused-vars
  const [validate, coa, coaSetter, errorText, currCategory, category, _isAlreadyTracked, children] =
    usePlannedCategoryChecker({
      id: parentCoaId,
      startDate,
      isEdit: true,
      isOneTime: editType === 'onetime',
    });
  const hasRecurringChanged = recurring ? editType === 'onetime' : editType === 'monthly';
  const isDisabled =
    realTargetAmount === targetAmountState && name === nameState && coa?.id === category?.id && !hasRecurringChanged;
  const isDisabledOrError = isDisabled || !!errorText;
  useEffect(() => {
    setName(name);
    setTarget(realTargetAmount);
    setEditType(recurring ? 'monthly' : 'onetime');
  }, [name, realTargetAmount, recurring]);

  useEffect(() => {
    validate(coa, plannedItem); // whenever edit type is changed, we call the setter to refresh error text
  }, [editType, coa, plannedItem, validate]);

  // const editHide = () => {
  //   setName(name);
  //   setTarget(realTargetAmount);
  //   setEditType(recurring ? 'monthly' : 'onetime');
  //   coaSetter({ type: 'CATEGORY', id: category?.id, parentId: category?.parentId });
  // };

  const dispatch = useDispatch();

  const removeCategoryFromSuggestedList = () => {
    if (suggestedCategories && setSuggestedCategories && suggestedCategories?.includes(currCategory?.knownCategoryId)) {
      setSuggestedCategories(suggestedCategories?.filter((item) => item !== currCategory?.knownCategoryId));
    }
  };

  const updateF2S = () => {
    const changed = [];
    if ((targetAmountState !== 0 && !targetAmountState) || targetAmountState < 0 || !coa.id) {
      return;
    }
    const item = plannedItems.get(cardIndex);
    if (!item) {
      return;
    }
    if (nameState !== item.name) {
      changed.push('name');
      item.name = nameState;
    }
    if (coa?.id !== category?.id) {
      changed.push('category');
      dispatch(
        filtersActions.updateFilter({
          id: filterId,
          filterItems: [
            {
              type: 'CHARTOFACCOUNT',
              chartOfAccounts: [coa?.toJS?.() || coa, ...(children?.map(({ id }) => ({ id, type: 'CATEGORY' })) ?? [])],
            },
          ],
        }),
      );
    }

    if (targetAmountState !== realTargetAmount) {
      changed.push('amount');
      if (item.overwrittenTargetAmount || item.overwrittenTargetAmount === 0) {
        item.resetOverwrittenTargetAmount = true;
        if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) delete item.overwrittenTargetAmount;
      }
      item.targetAmount = targetAmountState;
    }
    if (hasRecurringChanged) {
      changed.push('frequency');
      item.recurring = !recurring;
      if (editType !== 'monthly') {
        item.resetOverwrittenTargetAmount = true;
        item.targetAmount = item.overwrittenTargetAmount;
        if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) delete item.overwrittenTargetAmount;
      }
    }
    tracker.track(tracker.events.budgetItemEdit, {
      location,
      fields: changed,
    });
    const spendingItems = plannedItems.splice(cardIndex, 1, item); // TODO: change this to rely on first finding the item

    let total = 0;
    spendingItems?.forEach((plannedValue) => {
      const spentAmount = -1 * plannedValue.calculatedSpentAmount;
      if (
        (plannedValue.overwrittenTargetAmount === 0 || plannedValue.overwrittenTargetAmount) &&
        plannedValue.overwrittenTargetAmount >= spentAmount
      ) {
        total += plannedValue.overwrittenTargetAmount;
      } else if (plannedValue.targetAmount > spentAmount) {
        total += plannedValue.targetAmount;
      } else {
        total += spentAmount;
      }
    });
    const leftToSpendAmount = leftToSpend - calculatedPlannedAmount - total;

    dispatch(
      freeToSpendActions.updateFreeToSpend({
        id: freeToSpendId,
        plannedSpendingItems: spendingItems,
        calculatedPlannedSpendingAmount: -1 * total,
        leftToSpendAmount,
      }),
    );
    removeCategoryFromSuggestedList();
  };

  const setPlannedName = useCallback((e) => {
    setName(e.target.value);
  }, []);

  const onHandleClose = (event, reason) => {
    if (typeof event?.stopPropagation === 'function') {
      event?.stopPropagation();
    }
    if (typeof event?.preventDefault === 'function') {
      event?.preventDefault();
    }
    if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
      // update
      updateF2S();
    }
    dispatch(rootUiActions.removeDialog());
  };

  const filterFn = (c) =>
    c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
    (c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isExpenseCat(null, c.id)) ||
    c.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED; // only expense categories or uncat

  const formView = (
    <>
      <div className={classes.catAndAmt}>
        <CategoryField
          variant='bodyRegular'
          id='monthly'
          editable
          label='Category'
          textFieldVariant='filled'
          filterFn={filterFn}
          value={coa}
          onChange={(c) => coaSetter(c, plannedItem)}
          width='237px'
          margin='normal'
          error={!!errorText}
          helperText={errorText}
          textFieldProps={{
            label: 'Category',
            variant: 'filled',
            margin: 'normal',
          }}
        />
        <div className={classes.spacer} />
        <AmountField
          editable
          id='target-amount'
          variant={'filled'}
          label={'Amount'}
          marginProp={'normal'}
          value={targetAmountState}
          onChange={(e) => setTarget(Number(e.target.value))}
          textFieldVariant={'filled'}
          className={classes.amountField}
        />
      </div>
      {!errorText && (
        <Typography variant='paragraphRegular' className={cx(classes.helperText, classes.textSecondaryColor)}>
          Spending in this category (and sub-categories) will be applied to the allotted funds.
        </Typography>
      )}
      <TextField
        margin='normal'
        id='name'
        label={'Expense name (optional)'}
        inputProps={{ maxLength: 30 }}
        variant='filled'
        value={nameState}
        onChange={setPlannedName}
        className={classes.widthOfTextField}
      />
      <Typography variant='paragraphRegular' className={classes.textSecondaryColor}>
        Category name will be used if no custom name is set.
      </Typography>
      <RadioGroup
        aria-label='quiz'
        name='quiz'
        value={editType}
        className={classes.radio}
        onChange={(e) => setEditType(e.target.value)}
      >
        <FormControlLabel value='onetime' control={<Radio id={'edit-one-time-button'} />} label='One-time' />
        <FormControlLabel value='monthly' control={<Radio id={'edit-monthly-button'} />} label='Monthly' />
      </RadioGroup>
    </>
  );

  return (
    <StdDialog
      classes={{ root: classes.dialog }}
      onClose={onHandleClose}
      onKeyDown={(e) => {
        if (e.code === 'Enter' && !isDisabledOrError) {
          // editHide();
          onHandleClose(e, 'ENTER_KEY_DOWN');
        }
      }}
      open
      showCloseButton
      size='md'
      title='Edit planned item'
    >
      <DialogContent className={classes.dialogContent}>{formView}</DialogContent>
      <DialogActions>
        <div className={classes.info}>
          <InfoOutlinedIcon />
          <Typography variant='paragraphRegular' className={classes.paddingLeft}>
            {`${numberUtils.formatNumber(parseFloat(Math.abs(spendingThisMonth)), 'USD', '0,0.00')} spent in ${
              currCategory?.name
            } this month.`}
          </Typography>
        </div>
        <QButton
          disabled={!doAllItemsExist}
          variant='delete'
          type='submit'
          id={'edit-delete-button'}
          onClick={() => {
            dispatch(rootUiActions.removeDialog());
            dispatch(deletePlannedSpendingDialog(plannedItem.filterId, freeToSpendId));
          }}
        >
          DELETE
        </QButton>
        <QButton
          variant='contained'
          type='submit'
          id={'update-button'}
          onClick={(e) => {
            onHandleClose(e, 'SUBMIT_BUTTON');
          }}
          disabled={isDisabledOrError}
        >
          UPDATE
        </QButton>
      </DialogActions>
    </StdDialog>
  );
}

EditPlannedSpending.propTypes = {
  cardIndex: PropTypes.number,
  freeToSpendId: PropTypes.string,
  startDate: PropTypes.string,
  filterId: PropTypes.string,
  spendingThisMonth: PropTypes.number,
  location: PropTypes.string,
};

export default React.memo(EditPlannedSpending);
